import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiLockClosed,
  HiHeart,
  HiArrowPath,
  HiExclamationCircle,
  HiOutlineHeart,
  HiOutlineXMark,
  HiOutlineGlobeAlt,
  HiOutlineShoppingBag,
  HiOutlineDocumentText,
  HiCheck,
} from "react-icons/hi2";
import { FiDownload } from "react-icons/fi";
import RecipeCard from "../../components/cards/RecipeCard.jsx";
import { useRecipePopup } from "../../contexts/RecipePopupContext.jsx";
import { useLanguage } from "../../contexts/LanguageContext";
import { useRecipesTranslation } from "../../components/common/RecipeTranslator";
import { glassPanel } from "../../components/layout/UserLayout/designTokens";
import { specialCuisines } from "../WorldMenus/data/worldMenusData";
import { downloadRecipePdf } from "../../utils/recipePdf.js";
import { CardGridLoading } from "../../components/common/CardGridLoading";
import { translateBatch } from "../../utils/translationAPI";

/**
 * Page des favoris
 * Affiche toutes les recettes favorites de l'utilisateur
 */
const FavoritesPage = ({ user, token }) => {
  const navigate = useNavigate();
  const { openRecipePopup } = useRecipePopup();
  const { t, language } = useLanguage();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [worldMenuFavorites, setWorldMenuFavorites] = useState([]);
  const [worldPdfLoading, setWorldPdfLoading] = useState(false);
  const [selectedWorldMeal, setSelectedWorldMeal] = useState(null);
  const [translatedWorldMeal, setTranslatedWorldMeal] = useState(null);
  const [translatingWorldModal, setTranslatingWorldModal] = useState(false);
  const [translatedWorldMealsMap, setTranslatedWorldMealsMap] = useState({});

  /**
   * Charger les favoris
   */
  useEffect(() => {
    if (user && token) {
      loadFavorites();
    }

    // Charger aussi les favoris Menus du Monde depuis localStorage
    try {
      const storedWorld = JSON.parse(
        localStorage.getItem("worldMenusFavoriteItems") || "[]",
      );
      setWorldMenuFavorites(Array.isArray(storedWorld) ? storedWorld : []);
    } catch {
      setWorldMenuFavorites([]);
    }
  }, [user, token]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch("http://localhost:3000/api/favorites", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFavorites(data.data || []);
      } else {
        setError(t("errorLoadingFavorites"));
      }
    } catch (error) {
      console.error("Erreur lors du chargement des favoris:", error);
      setError(t("connectionError"));
    } finally {
      setLoading(false);
    }
  };

  const toggleWorldMenuFavorite = (mealId) => {
    setWorldMenuFavorites((prev) => {
      const next = prev.filter((m) => m.idMeal !== mealId);
      try {
        localStorage.setItem("worldMenusFavoriteItems", JSON.stringify(next));
        const map =
          JSON.parse(localStorage.getItem("worldMenusFavorites") || "{}") || {};
        map[mealId] = false;
        localStorage.setItem("worldMenusFavorites", JSON.stringify(map));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  };

  const buildFullWorldMeal = (meal) => {
    // Essayer de retrouver une recette personnalisée (khmer, korean, etc.)
    if (typeof meal.idMeal === "string") {
      const custom = specialCuisines
        .flatMap((c) => c.customRecipes || [])
        .find((r) => r.idMeal === meal.idMeal);
      if (custom) {
        const mealWithIngredients = {
          ...custom,
          strInstructions:
            custom.instructions ||
            custom.description ||
            "Recette traditionnelle.",
        };
        if (custom.ingredients) {
          custom.ingredients.forEach((ing, index) => {
            mealWithIngredients[`strIngredient${index + 1}`] = ing.name;
            mealWithIngredients[`strMeasure${index + 1}`] = ing.measure;
          });
        }
        return mealWithIngredients;
      }
    }
    // Sinon, retourner le résumé tel quel (au pire, moins d'infos)
    return meal;
  };

  const handleDownloadWorldMealPdf = async (meal) => {
    if (!meal) return;
    try {
      setWorldPdfLoading(true);
      const fullMeal = buildFullWorldMeal(meal);

      const ingredientsList = Array.from({ length: 20 }, (_, i) => i + 1)
        .map((i) => {
          const ing = fullMeal[`strIngredient${i}`];
          const measure = fullMeal[`strMeasure${i}`];
          return ing && ing.trim()
            ? `${(measure || "").trim()} ${ing.trim()}`.trim()
            : null;
        })
        .filter(Boolean);

      const instructionsText = fullMeal.strInstructions || "";
      const instructionsList = instructionsText
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);

      const recipeLike = {
        title: fullMeal.strMeal,
        description: [fullMeal.strCategory, fullMeal.strArea]
          .filter(Boolean)
          .join(" • "),
        ingredients: ingredientsList.join("\n"),
        instructions: instructionsList.join("\n"),
        image_url: fullMeal.strMealThumb,
        prep_time: null,
        cook_time: null,
        servings: null,
        difficulty: "",
        category: fullMeal.strCategory || "",
        area: fullMeal.strArea || "",
      };

      await downloadRecipePdf(recipeLike, {
        ingredientsList,
        instructionsList,
        labels: {
          prep: t("preparation"),
          cook: t("cooking"),
          servings: t("servings"),
          difficulty: t("difficulty") || "Difficulté",
          ingredients: t("ingredients"),
          instructions: t("instructions"),
        },
      });
    } finally {
      setWorldPdfLoading(false);
    }
  };

  // Traduire le contenu de la modal World Menus favoris
  useEffect(() => {
    if (!selectedWorldMeal || !language) {
      setTranslatedWorldMeal(null);
      setTranslatingWorldModal(false);
      return;
    }
    setTranslatingWorldModal(true);
    const m = selectedWorldMeal;
    const instructionLines = (m.strInstructions || "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const ingredientTexts = [];
    const measureTexts = [];
    for (let i = 1; i <= 20; i++) {
      const ing = m[`strIngredient${i}`];
      if (ing && ing.trim()) {
        ingredientTexts.push(ing.trim());
        measureTexts.push((m[`strMeasure${i}`] || "").trim());
      }
    }
    const allTexts = [
      m.strMeal,
      m.strCategory,
      m.strArea,
      ...instructionLines,
      ...ingredientTexts,
      ...measureTexts,
    ];
    translateBatch(allTexts, language, "auto")
      .then((results) => {
        const n = ingredientTexts.length;
        const nInst = instructionLines.length;
        const [strMeal, strCategory, strArea, ...rest] = results;
        const instResults = rest.slice(0, nInst);
        const ingTrans = rest.slice(nInst, nInst + n);
        const measureTrans = rest.slice(nInst + n, nInst + n + n);
        const strInstructions = instructionLines
          .map((line, i) => instResults[i] ?? line)
          .join("\n\n");
        const ingredients = ingredientTexts.map((name, idx) =>
          `${(measureTrans[idx] ?? measureTexts[idx] ?? "").trim()} ${ingTrans[idx] ?? name}`.trim(),
        );
        setTranslatedWorldMeal({
          strMeal: strMeal && strMeal.trim() ? strMeal : m.strMeal,
          strCategory:
            strCategory && strCategory.trim() ? strCategory : m.strCategory,
          strArea: strArea && strArea.trim() ? strArea : m.strArea,
          strInstructions,
          ingredients,
        });
      })
      .catch(() => {
        setTranslatedWorldMeal(null);
      })
      .finally(() => {
        setTranslatingWorldModal(false);
      });
  }, [selectedWorldMeal, language]);

  // Traduire les titres des cartes World Menus favoris
  useEffect(() => {
    if (!worldMenuFavorites.length || !language) {
      setTranslatedWorldMealsMap({});
      return;
    }
    const texts = worldMenuFavorites.flatMap((m) => [
      m.strMeal,
      m.strCategory || "",
      m.strArea || "",
    ]);
    translateBatch(texts, language, "auto").then((results) => {
      const next = {};
      worldMenuFavorites.forEach((m, i) => {
        next[m.idMeal] = {
          strMeal: results[i * 3] || m.strMeal,
          strCategory: results[i * 3 + 1] || m.strCategory,
          strArea: results[i * 3 + 2] || m.strArea,
        };
      });
      setTranslatedWorldMealsMap(next);
    });
  }, [worldMenuFavorites, language]);

  const selectedWorldIngredients = selectedWorldMeal
    ? Array.from({ length: 20 }, (_, i) => i + 1)
        .map((i) => {
          const ing = selectedWorldMeal[`strIngredient${i}`];
          const measure = selectedWorldMeal[`strMeasure${i}`];
          return ing && ing.trim()
            ? `${(measure || "").trim()} ${ing.trim()}`.trim()
            : null;
        })
        .filter(Boolean)
    : [];

  const selectedWorldInstructions = selectedWorldMeal
    ? (selectedWorldMeal.strInstructions || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
    : [];

  /**
   * Traduction automatique des recettes favorites
   */
  const { translatedRecipes: translatedFavorites, isTranslating } =
    useRecipesTranslation(favorites);

  /**
   * Gérer la suppression d'un favori
   */
  const handleRemoveFavorite = async (recipeId) => {
    try {
      const response = await fetch(
        `http://localhost:3000/api/favorites/remove/${recipeId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        // Recharger les favoris
        loadFavorites();
      } else {
        alert(t("errorDeletingFavorite"));
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert(t("errorDeletingFavorite"));
    }
  };

  /**
   * Gérer l'ajout d'un favori
   */
  const handleAddFavorite = async (recipeId, isFavorite) => {
    if (isFavorite) {
      // Ajouter aux favoris
      try {
        const response = await fetch(
          `http://localhost:3000/api/favorites/add/${recipeId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.ok) {
          // Recharger les favoris
          loadFavorites();
        }
      } catch (error) {
        console.error("Erreur lors de l'ajout:", error);
      }
    } else {
      // Retirer des favoris
      handleRemoveFavorite(recipeId);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {t("unauthorizedAccess")}
          </h2>
          <p className="text-white mb-6">{t("mustBeLoggedInForFavorites")}</p>
          <button
            onClick={() => navigate("/auth")}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
          >
            {t("login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full py-4 sm:py-6 w-full min-w-0">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* En-tête */}
        <div className={`${glassPanel} p-4 sm:p-6 mb-4 sm:mb-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2 flex items-center gap-2">
                <HiHeart
                  className="w-7 h-7 sm:w-8 sm:h-8 text-red-400 flex-shrink-0"
                  aria-hidden
                />
                {t("myFavorites")}
              </h1>
              <p className="text-white/90 text-sm sm:text-base">
                {t("yourFavoriteRecipes")}
              </p>
            </div>
            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-white">
                  {favorites.length}
                </div>
                <div className="text-xs sm:text-sm text-white/90">
                  {t("favorites")}
                </div>
              </div>
              <button
                onClick={loadFavorites}
                className="px-4 py-2 bg-white/20 dark:bg-gray-700/50 text-white rounded-lg hover:bg-white/30 dark:hover:bg-gray-600/50 transition-colors border border-slate-200/50 dark:border-slate-600/50 text-sm sm:text-base inline-flex items-center gap-2"
              >
                <HiArrowPath className="w-4 h-4" aria-hidden />
                {t("refresh")}
              </button>
            </div>
          </div>
        </div>

        {/* Contenu */}
        {loading || (isTranslating && favorites.length > 0) ? (
          <div className={`${glassPanel} p-8`}>
            <CardGridLoading
              message={t("loadingFavorites")}
              gridCols="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              count={6}
            />
          </div>
        ) : error ? (
          <div className={`${glassPanel} p-8`}>
            <div className="text-center">
              <div className="text-6xl mb-4">😞</div>
              <h3 className="text-xl font-semibold text-white mb-2">Erreur</h3>
              <p className="text-white mb-4">{error}</p>
              <button
                onClick={loadFavorites}
                className="px-6 py-3 bg-white/20 dark:bg-gray-700/50 text-white rounded-xl font-semibold hover:bg-white/30 dark:hover:bg-gray-600/50 transition-colors border border-slate-200/50 dark:border-slate-600/50"
              >
                Réessayer
              </button>
            </div>
          </div>
        ) : favorites.length > 0 || worldMenuFavorites.length > 0 ? (
          <>
            {favorites.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-white mb-3">
                  {t("yourFavoriteRecipes")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {translatedFavorites.map((recipe) => (
                    <RecipeCard
                      key={recipe.id}
                      recipe={recipe}
                      user={user}
                      onFavoriteToggle={handleAddFavorite}
                      onEdit={(recipe) => {
                        console.log("Modifier la recette:", recipe);
                        navigate(`/edit-recipe/${recipe.id}`);
                      }}
                      onDelete={async (recipeId) => {
                        if (window.confirm(t("confirmDelete"))) {
                          try {
                            const response = await fetch(
                              `http://localhost:3000/api/recipes/${recipeId}`,
                              {
                                method: "DELETE",
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                },
                              },
                            );

                            if (response.ok) {
                              console.log(t("recipeDeleted"));
                              loadFavorites();
                            } else {
                              alert(t("errorDeleting"));
                            }
                          } catch (error) {
                            console.error(
                              "Erreur lors de la suppression:",
                              error,
                            );
                            alert(t("errorDeleting"));
                          }
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {worldMenuFavorites.length > 0 && (
              <div className="mt-4">
                <h2 className="text-lg font-semibold text-white mb-3">
                  {t("worldMenusTitle") || "Menus du Monde favoris"}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {worldMenuFavorites.map((meal) => (
                    <article
                      key={meal.idMeal}
                      className="group relative bg-white/10 backdrop-blur rounded-xl sm:rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl hover:border-orange-300/50 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full cursor-pointer"
                      onClick={() =>
                        setSelectedWorldMeal(buildFullWorldMeal(meal))
                      }
                    >
                      <div className="relative aspect-square rounded-lg sm:rounded-xl overflow-hidden mb-2 sm:mb-3 bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200 shadow-inner">
                        {meal.strMealThumb ? (
                          <img
                            src={meal.strMealThumb}
                            alt={meal.strMeal}
                            className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700 ease-out"
                          />
                        ) : null}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                      </div>
                      <div className="p-3 sm:p-4 flex flex-col flex-1">
                        <h3 className="font-bold text-white uppercase text-xs sm:text-sm mb-1 line-clamp-2">
                          {translatedWorldMealsMap[meal.idMeal]?.strMeal ??
                            meal.strMeal}
                        </h3>
                        <span className="inline-block px-2.5 py-0.5 bg-orange-100/90 text-orange-800 text-xs font-semibold rounded-full w-fit mb-2">
                          {(translatedWorldMealsMap[meal.idMeal]?.strCategory ??
                            meal.strCategory) ||
                            "World menu"}
                        </span>
                        {(translatedWorldMealsMap[meal.idMeal]?.strArea ??
                          meal.strArea) && (
                          <p className="text-[11px] text-white/80 mb-2">
                            {translatedWorldMealsMap[meal.idMeal]?.strArea ??
                              meal.strArea}
                          </p>
                        )}
                        <div className="mt-auto flex items-center justify-between pt-1">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleWorldMenuFavorite(meal.idMeal);
                            }}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white bg-red-500/90 hover:bg-red-600 transition-all duration-200"
                            title={
                              t("removeFromFavorites") || "Retirer des favoris"
                            }
                          >
                            <HiHeart className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadWorldMealPdf(meal);
                            }}
                            disabled={worldPdfLoading}
                            className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/30 disabled:opacity-60 flex items-center justify-center"
                            title={
                              t("downloadPdf") || "Télécharger la fiche PDF"
                            }
                          >
                            <FiDownload className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={`${glassPanel} p-8`}>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 text-red-400/90 mb-4">
                <HiOutlineHeart className="w-10 h-10" aria-hidden />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {t("noFavorites")}
              </h3>
              <p className="text-white mb-6">{t("noFavoritesYet")}</p>
              <button
                onClick={() => navigate("/dashboard")}
                className="px-6 py-3 bg-white/20 dark:bg-gray-700/50 text-white rounded-xl font-semibold hover:bg-white/30 dark:hover:bg-gray-600/50 transition-colors border border-slate-200/50 dark:border-slate-600/50"
              >
                {t("discoverRecipes")}
              </button>
            </div>
          </div>
        )}
      </div>
      {selectedWorldMeal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedWorldMeal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative bg-slate-900/95 border border-slate-700 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedWorldMeal(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 bg-slate-800/90 rounded-full flex items-center justify-center text-slate-100 hover:bg-red-500/90 hover:text-white transition-colors"
              aria-label="Fermer"
            >
              <HiOutlineXMark className="w-5 h-5" />
            </button>

            <div className="relative h-52 sm:h-64 overflow-hidden rounded-t-2xl">
              {selectedWorldMeal.strMealThumb && (
                <img
                  src={selectedWorldMeal.strMealThumb}
                  alt={selectedWorldMeal.strMeal}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <h2 className="text-2xl sm:text-3xl font-bold drop-shadow">
                  {translatedWorldMeal?.strMeal ?? selectedWorldMeal.strMeal}
                </h2>
                <p className="flex items-center gap-2 mt-1 text-sm sm:text-base text-white/95">
                  <HiOutlineGlobeAlt className="w-5 h-5 shrink-0 opacity-90" />
                  <span>
                    {(translatedWorldMeal?.strArea ??
                      selectedWorldMeal.strArea) ||
                      "World"}{" "}
                    {(translatedWorldMeal?.strCategory ??
                    selectedWorldMeal.strCategory)
                      ? `• ${translatedWorldMeal?.strCategory ?? selectedWorldMeal.strCategory}`
                      : ""}
                  </span>
                </p>
              </div>
            </div>

            <div className="relative p-5 sm:p-8">
              {translatingWorldModal && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-slate-900/90 rounded-2xl min-h-[200px]">
                  <div className="w-10 h-10 rounded-full border-2 border-orange-500/30 border-t-orange-500 animate-spin" />
                  <p className="text-white font-medium">{t("translating")}</p>
                </div>
              )}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3 text-white flex items-center gap-2">
                  <HiOutlineDocumentText className="w-5 h-5 shrink-0 text-orange-400" />
                  {t("instructions")}
                </h3>
                <p className="text-slate-100/90 leading-relaxed whitespace-pre-line text-sm sm:text-base">
                  {translatedWorldMeal?.strInstructions ??
                    selectedWorldInstructions.join("\n")}
                </p>
              </div>

              <div>
                <h3 className="text-lg font-bold mb-3 text-white flex items-center gap-2">
                  <HiOutlineShoppingBag className="w-5 h-5 shrink-0 text-orange-400" />
                  {t("ingredients")}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(
                    translatedWorldMeal?.ingredients ?? selectedWorldIngredients
                  ).map((line, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 bg-slate-800/80 px-3 py-2 rounded-xl text-sm"
                    >
                      <HiCheck className="w-4 h-4 text-orange-400 shrink-0" />
                      <span className="text-slate-100">{line}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => handleDownloadWorldMealPdf(selectedWorldMeal)}
                  disabled={worldPdfLoading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-60"
                >
                  <FiDownload className="w-5 h-5" />
                  {t("downloadPdf") || "Télécharger la fiche PDF"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesPage;
